import React, { useEffect, useMemo, useState } from 'react'
import { Room } from 'livekit-client'

interface DeviceSettingsModalProps {
  open: boolean
  onClose: () => void
  selectedMicId: string
  selectedCamId: string
  selectedSpeakerId: string
  onChangeMic: (e: React.ChangeEvent<HTMLSelectElement>) => void
  onChangeCam: (e: React.ChangeEvent<HTMLSelectElement>) => void
  onChangeSpeaker: (e: React.ChangeEvent<HTMLSelectElement>) => void
  refreshKey?: number
}

export function DeviceSettingsModal({ 
  open, 
  onClose, 
  selectedMicId, 
  selectedCamId, 
  selectedSpeakerId, 
  onChangeMic, 
  onChangeCam, 
  onChangeSpeaker, 
  refreshKey 
}: DeviceSettingsModalProps) {
  const [micDevices, setMicDevices] = useState<MediaDeviceInfo[]>([])
  const [camDevices, setCamDevices] = useState<MediaDeviceInfo[]>([])
  const [speakerDevices, setSpeakerDevices] = useState<MediaDeviceInfo[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    let mounted = true
    setIsLoading(true)
    setError(null)

    const loadDevices = async () => {
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true, video: true })
        const [mics, cams, outs] = await Promise.all([
          Room.getLocalDevices('audioinput'),
          Room.getLocalDevices('videoinput'),
          Room.getLocalDevices('audiooutput'),
        ])
        if (!mounted) return
        setMicDevices(mics)
        setCamDevices(cams)
        setSpeakerDevices(outs)
      } catch (e) {
        console.error('Failed to get LiveKit devices, falling back to standard API:', e)
        try {
          const devices = await navigator.mediaDevices.enumerateDevices()
          if (!mounted) return
          setMicDevices(devices.filter((d) => d.kind === 'audioinput'))
          setCamDevices(devices.filter((d) => d.kind === 'videoinput'))
          setSpeakerDevices(devices.filter((d) => d.kind === 'audiooutput'))
        } catch (fallbackError) {
          console.error('Failed to enumerate devices:', fallbackError)
          if (!mounted) return
          setError('Failed to load devices. Please check permissions.')
        }
      } finally {
        if (mounted) setIsLoading(false)
      }
    }

    loadDevices()
    return () => { mounted = false }
  }, [open, refreshKey])

  const derivedMicId = useMemo(() => selectedMicId || micDevices[0]?.deviceId || '', [selectedMicId, micDevices])
  const derivedCamId = useMemo(() => selectedCamId || camDevices[0]?.deviceId || '', [selectedCamId, camDevices])
  const derivedSpkId = useMemo(() => selectedSpeakerId || speakerDevices[0]?.deviceId || '', [selectedSpeakerId, speakerDevices])

  if (!open) return null

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-4" onClick={onClose}>
      <div 
        className="w-full max-w-[480px] bg-neutral-900 border border-neutral-700 rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.4)] overflow-hidden" 
        role="dialog" 
        aria-modal="true" 
        aria-labelledby="config-title" 
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 pt-6 pb-0 mb-6">
          <h2 id="config-title" className="m-0 text-white text-[20px] font-semibold flex items-center gap-2">Device Settings</h2>
          <button className="w-8 h-8 border-0 bg-white/10 text-white rounded-lg cursor-pointer flex items-center justify-center text-[16px] transition-all hover:bg-white/20 hover:scale-105" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        {error && (
          <div className="px-6 mb-4">
            <div className="px-6 py-3 bg-[rgba(220,53,69,0.1)] border border-[rgba(220,53,69,0.3)] rounded-lg text-[#dc3545]">
              {error}
            </div>
          </div>
        )}

        <div className="px-6 grid gap-5">
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-200 flex items-center gap-2">
              <span className="text-[16px] w-5 flex items-center justify-center">🎤</span>
              Microphone
            </label>
            {micDevices.length > 0 ? (
              <select 
                className="w-full px-4 py-3 bg-neutral-800 border border-neutral-600 rounded-lg text-white text-sm cursor-pointer transition-all pr-10 focus:outline-none focus:border-blue-500 hover:border-neutral-500"
                value={derivedMicId} 
                onChange={onChangeMic}
                disabled={isLoading}
              >
                {micDevices.map((device) => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {device.label || `Microphone ${device.deviceId.slice(0, 8)}`}
                  </option>
                ))}
              </select>
            ) : (
              <div className="text-gray-400 italic px-4 py-3 bg-neutral-800 border border-neutral-600 rounded-lg text-center">
                {isLoading ? 'Loading microphones...' : 'No microphones found'}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-200 flex items-center gap-2">
              <span className="text-[16px] w-5 flex items-center justify-center">📹</span>
              Camera
            </label>
            {camDevices.length > 0 ? (
              <select 
                className="w-full px-4 py-3 bg-neutral-800 border border-neutral-600 rounded-lg text-white text-sm cursor-pointer transition-all pr-10 focus:outline-none focus:border-blue-500 hover:border-neutral-500"
                value={derivedCamId} 
                onChange={onChangeCam}
                disabled={isLoading}
              >
                {camDevices.map((device) => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {device.label || `Camera ${device.deviceId.slice(0, 8)}`}
                  </option>
                ))}
              </select>
            ) : (
              <div className="text-gray-400 italic px-4 py-3 bg-neutral-800 border border-neutral-600 rounded-lg text-center">
                {isLoading ? 'Loading cameras...' : 'No cameras found'}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-200 flex items-center gap-2">
              <span className="text-[16px] w-5 flex items-center justify-center">🔊</span>
              Speaker
            </label>
            {speakerDevices.length > 0 ? (
              <select 
                className="w-full px-4 py-3 bg-neutral-800 border border-neutral-600 rounded-lg text-white text-sm cursor-pointer transition-all pr-10 focus:outline-none focus:border-blue-500 hover:border-neutral-500"
                value={derivedSpkId} 
                onChange={onChangeSpeaker}
                disabled={isLoading}
              >
                {speakerDevices.map((device) => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {device.label || `Speaker ${device.deviceId.slice(0, 8)}`}
                  </option>
                ))}
              </select>
            ) : (
              <div className="text-gray-400 italic px-4 py-3 bg-neutral-800 border border-neutral-600 rounded-lg text-center">
                {isLoading ? 'Loading speakers...' : 'Speaker selection not supported'}
              </div>
            )}
          </div>
        </div>

        <div className="px-6 py-6 flex justify-end gap-3 border-t border-neutral-800 mt-6">
          <button 
            className="min-w-20 px-5 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium transition-all hover:bg-blue-700 disabled:opacity-50"
            onClick={onClose}
            disabled={isLoading}
          >
            {isLoading ? 'Loading...' : 'Done'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default DeviceSettingsModal
